import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  QUARTERLY_OKR,
  QUARTERLY_OKR_SCORECARD,
  QUARTERLY_OKR_SUMMARY,
} from "../../../../data/RouteUrls";
import { getProgressBarColour } from "../../../../utils/generalUtils";
import { readError } from "../_config/quarterlyOkrConfig";
import {
  briefRoute,
  quarterCrumbs,
  quarterRowOf,
  routeForQuarter,
} from "../_config/quarterlyOkrRoutes";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import QokrScorecardCard, {
  objectivePercent,
  percentInk,
} from "../_shared/QokrScorecardCard/QokrScorecardCard";
// The Annual scorecard's `okrs-*` sheet — at this app's path; the shared card
// relies on its page to bring it.
import QokrQuarterSelect from "../_shared/QokrQuarterSelect/QokrQuarterSelect";
import QokrPersonSelect from "../_shared/QokrPersonSelect/QokrPersonSelect";
import "../../AnnualOkr/AnnualOkrViewOkrs/_okr-scorecard.scss";
import "../_shared/QokrScorecardCard/_qokr-scorecard-page.scss";
import "./_quarterly-okr-scorecard.scss";

// ═══════════════════════════════════════════════════════════════════════════
// `/coach-panel/quarterly-okrs/:quarterCompanyId/scorecard` — a LOCKED
// quarter, read only: the same Quarterly OKR Scorecard the company tracks on
// (the CEO app's `QuarterlyOkrTracking`, same shared card), so coach and
// client look at one board.
//
// DATA: `GET /coach/quarterly-okr/:quarterCompanyId/tracking` — the §11
// tracking shape (key results with `progress`) with every `can_edit_*` false
// (handoff 32 §1.2). If the read fails, the page offers the quarter's summary
// instead of a blank board.
//
// Before this page existed, a locked quarter went to the summary — see
// `routeForQuarter` and the live review's lock handler, which now come here.
// ═══════════════════════════════════════════════════════════════════════════

const TAB = { COMPANY: "company", INDIVIDUAL: "individual" };

const selectTracking = (state) => state.trackingByQc;
const selectClients = (state) => state.clients;

// Individual objectives, in the order they sit, each remembering the company
// objective it hangs under (its link chip).
const individualsOf = (tracking) => {
  const list = [];
  (tracking?.company_objectives || []).forEach((company) =>
    (company.individual_objectives || []).forEach((objective) =>
      list.push({ ...objective, parentObjective: company.objective }),
    ),
  );
  (tracking?.not_linked || []).forEach((objective) =>
    list.push({ ...objective, parentObjective: null }),
  );
  return list;
};

const personOf = (objective) => objective.member || objective.owner || null;

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const QuarterlyOkrScorecard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const HeaderMetaData = useContext(HeaderContext);
  const { quarterCompanyId: qcId } = useParams();
  const tracking = useQuarterlyOkrStore(selectTracking)[qcId];
  const clients = useQuarterlyOkrStore(selectClients);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState(TAB.COMPANY);
  const [personId, setPersonId] = useState(null);

  const quarterRow = quarterRowOf(clients, qcId);
  const client = (clients || []).find((c) =>
    (c.quarters || []).some(
      (q) => String(q.quarter_company_id) === String(qcId),
    ),
  );
  const quarter = client?.quarters.find(
    (q) => String(q.quarter_company_id) === String(qcId),
  );

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKR Scorecard";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    if (!quarterlyOkrStore.getState().clients) {
      quarterlyOkrStore
        .getState()
        .fetchDashboard()
        .catch(() => null);
    }
    quarterlyOkrStore
      .getState()
      .fetchTracking(qcId)
      .then(() => setLoadError(null))
      .catch((error) => setLoadError(readError(error).message));
  }, [qcId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(
        QUARTERLY_OKR_SCORECARD,
        quarterCrumbs({
          root: breadCrumbsArray[QUARTERLY_OKR][0],
          qcId,
          row: quarterRow,
          companyName: client?.company_name,
          title: "Scorecard",
          fallbackTitle: "Scorecard",
        }),
      ),
    );
  }, [dispatch, client?.company_name, quarterRow, qcId]);

  // The quarter dropdown: this client's AI quarters from the dashboard
  // (§9.1, up to 3). Each opens wherever its run is — this scorecard if it is
  // locked too, else its review, live review or summary (`routeForQuarter`).
  // Always shown, even with one quarter, as on the CEO's scorecard.
  const quarterList = (client?.quarters || []).filter((q) =>
    routeForQuarter(q),
  );
  const changeQuarter = (quarterCompanyId) => {
    const next = (client?.quarters || []).find(
      (q) => String(q.quarter_company_id) === String(quarterCompanyId),
    );
    if (!next || String(next.quarter_company_id) === String(qcId)) return;
    const route = routeForQuarter(next);
    if (route) history.push(route);
  };

  const companyObjectives = tracking?.company_objectives || [];
  const individuals = useMemo(() => individualsOf(tracking), [tracking]);
  const people = useMemo(() => {
    const seen = new Map();
    individuals.forEach((objective) => {
      const person = personOf(objective);
      if (person?.user_id == null) return;
      const key = String(person.user_id);
      const entry = seen.get(key) || { ...person, count: 0 };
      entry.count += 1;
      seen.set(key, entry);
    });
    return [...seen.values()];
  }, [individuals]);
  const shownIndividuals = personId
    ? individuals.filter(
        (objective) => String(personOf(objective)?.user_id) === personId,
      )
    : individuals;
  const overall = companyObjectives.length
    ? Math.round(
        companyObjectives.reduce((sum, o) => sum + objectivePercent(o), 0) /
          companyObjectives.length,
      )
    : 0;

  const card = (type, objective, index, { label, linkLabel }) => (
    <QokrScorecardCard
      key={`${type}:${objective.objective_id}`}
      objective={{ ...objective, type }}
      label={label}
      linkLabel={linkLabel}
      // Read only, whatever the payload's flags say.
      writable={false}
      defaultOpen={index === 0}
    />
  );

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            quarterRow?.brief_status ? briefRoute(qcId) : QUARTERLY_OKR,
          )
        }
        showBack
      />
      <div id="qokr-coach-scorecard">
        {loadError && !tracking ? (
          <div className="qokr-coach-scorecard__error">
            <i className="fa-regular fa-file-lines" aria-hidden="true" />
            <p className="qokr-coach-scorecard__error-title">
              The scorecard isn&apos;t available yet
            </p>
            <p className="qokr-coach-scorecard__error-text">{loadError}</p>
            <button
              type="button"
              className="qokr-coach-scorecard__error-btn"
              onClick={() =>
                history.push(
                  QUARTERLY_OKR_SUMMARY.replace(":quarterCompanyId", qcId),
                )
              }
            >
              View the quarter summary
            </button>
          </div>
        ) : (
          <div className="okrs-page qokr-sc-page">
            <div className="okrs-header">
              <div className="okrs-header-text">
                <h1 className="okrs-title">
                  Quarterly OKR Scorecard
                  {client?.company_name ? ` — ${client.company_name}` : ""}
                </h1>
                {quarter?.label ? (
                  <span className="okrs-duration">
                    <CalendarIcon />
                    {quarter.label}
                  </span>
                ) : null}
              </div>
              <div className="qokr-sc-header-right">
                {quarterList.length ? (
                  <QokrQuarterSelect
                    quarters={quarterList}
                    selectedId={qcId}
                    onChange={changeQuarter}
                  />
                ) : null}
                {companyObjectives.length ? (
                  <div className="qokr-sc-overall">
                    <span className="qokr-sc-overall-label">
                      <b>Company progress</b>
                      {companyObjectives.length === 1
                        ? "1 objective"
                        : `${companyObjectives.length} objectives`}
                    </span>
                    <span
                      className="okrs-ring"
                      style={{
                        background: `conic-gradient(${getProgressBarColour(overall)} ${overall}%, #eef0f4 0)`,
                      }}
                      role="img"
                      aria-label={`Company progress ${overall}%`}
                    >
                      <span
                        className="okrs-ring-inner"
                        style={{ color: percentInk(overall) }}
                      >
                        {overall}%
                      </span>
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Tabs only once the board is in — "Company OKRs 0" while it
                loads would read as an empty quarter. */}
            {tracking ? (
              <div className="qokr-sc-toolbar">
                <div
                  className="qokr-sc-tabs"
                  role="tablist"
                  aria-label="Scorecard"
                >
                  {[
                    [TAB.COMPANY, "Company OKRs", companyObjectives.length],
                    [TAB.INDIVIDUAL, "Individual OKRs", individuals.length],
                  ].map(([key, text, count]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={tab === key}
                      className={`qokr-sc-tab${tab === key ? " is-active" : ""}`}
                      onClick={() => setTab(key)}
                    >
                      {text}
                      <span className="qokr-sc-count">{count}</span>
                    </button>
                  ))}
                </div>

                {tab === TAB.INDIVIDUAL && people.length ? (
                  <QokrPersonSelect
                    people={people}
                    value={personId}
                    onChange={setPersonId}
                  />
                ) : null}
              </div>
            ) : null}

            {!tracking ? (
              <div className="okrs-list" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="okrs-card okrs-card--sk" />
                ))}
              </div>
            ) : (
              <div className="okrs-list qokr-sc-list">
                {tab === TAB.COMPANY ? (
                  companyObjectives.length ? (
                    companyObjectives.map((objective, index) =>
                      card("company", objective, index, {
                        label: `O${index + 1}`,
                        linkLabel:
                          objective.annual_okr ??
                          objective.link_label ??
                          undefined,
                      }),
                    )
                  ) : (
                    <p className="okrs-line-empty">No company objectives.</p>
                  )
                ) : shownIndividuals.length ? (
                  shownIndividuals.map((objective, index) =>
                    card("individual", objective, index, {
                      label: String(index + 1).padStart(2, "0"),
                      linkLabel: objective.parentObjective,
                    }),
                  )
                ) : (
                  <p className="okrs-line-empty">
                    No individual objectives for this person this quarter.
                  </p>
                )}
              </div>
            )}

            <p className="okrs-footnote">
              Shown in the order the team set them. Read only — the team updates
              progress and status.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarterlyOkrScorecard;
